import ReactMarkdown from "react-markdown";
import type { Components } from "react-markdown";

/**
 * SafeMarkdown — render an UNTRUSTED Markdown string (org-admin
 * authored consent template, annotation comment, etc.) as React
 * elements with NO raw-HTML escape hatch.
 *
 * Why no rehype-raw / no `allowHtml` switch: this component renders
 * content authored by ANY org admin, including ones we don't fully
 * trust to handle untrusted Markdown. ``react-markdown`` without
 * ``rehype-raw`` builds React elements straight from the Markdown
 * AST and never inserts arbitrary HTML, which means no path to
 * inject ``<script>`` / ``<iframe>`` / ``onerror=`` / ``javascript:``
 * URIs through the body. Any future caller that needs raw HTML
 * support should use a different component, not flip a flag here.
 *
 * URLs in links/images are filtered: only `http(s):`, `mailto:`, and
 * relative paths are allowed. ``javascript:`` and ``data:`` are
 * stripped to ``about:blank`` to neutralize XSS via crafted hrefs.
 */

const SAFE_URL = (url: string | null | undefined) => {
  if (!url) return "about:blank";
  const trimmed = url.trim();
  if (
    trimmed.startsWith("http://") ||
    trimmed.startsWith("https://") ||
    trimmed.startsWith("mailto:") ||
    trimmed.startsWith("/") ||
    trimmed.startsWith("#") ||
    trimmed.startsWith("./")
  ) {
    return trimmed;
  }
  return "about:blank";
};

const components: Components = {
  h1: (props) => <h2 style={{ fontSize: "var(--font-size-lead)", fontWeight: 600, margin: "20px 0 8px" }} {...props} />,
  h2: (props) => <h3 style={{ fontWeight: 600, margin: "18px 0 8px" }} {...props} />,
  h3: (props) => (
    <h4 style={{ fontSize: "var(--font-size-body-small)", fontWeight: 600, margin: "16px 0 6px" }} {...props} />
  ),
  h4: (props) => (
    <h5 style={{ fontSize: "var(--font-size-body-small)", fontWeight: 600, margin: "14px 0 6px" }} {...props} />
  ),
  h5: (props) => (
    <h6 style={{ fontSize: "var(--font-size-body-small)", fontWeight: 600, margin: "12px 0 4px" }} {...props} />
  ),
  h6: (props) => (
    <h6 style={{ fontSize: "var(--font-size-caption)", fontWeight: 600, margin: "12px 0 4px" }} {...props} />
  ),
  p: (props) => (
    <p style={{ margin: "0 0 12px", lineHeight: 1.55, fontSize: "var(--font-size-body-small)" }} {...props} />
  ),
  ul: (props) => (
    <ul
      style={{ margin: "0 0 12px", paddingLeft: 20, lineHeight: 1.55, fontSize: "var(--font-size-body-small)" }}
      {...props}
    />
  ),
  ol: (props) => (
    <ol
      style={{ margin: "0 0 12px", paddingLeft: 20, lineHeight: 1.55, fontSize: "var(--font-size-body-small)" }}
      {...props}
    />
  ),
  li: (props) => <li style={{ margin: "2px 0" }} {...props} />,
  blockquote: (props) => (
    <blockquote
      style={{
        margin: "0 0 12px",
        padding: "8px 12px",
        borderLeft: "3px solid var(--color-neutral-border, #ccc)",
        background: "var(--color-neutral-surface, rgba(0,0,0,0.02))",
        fontStyle: "italic",
        fontSize: "var(--font-size-body-small)",
      }}
      {...props}
    />
  ),
  code: (props) => (
    <code
      style={{
        background: "var(--color-neutral-surface, rgba(0,0,0,0.05))",
        padding: "1px 6px",
        borderRadius: 3,
        fontFamily: "monospace",
        fontSize: "var(--font-size-body-small)",
      }}
      {...props}
    />
  ),
  pre: (props) => (
    <pre
      style={{
        background: "var(--color-neutral-surface, rgba(0,0,0,0.05))",
        padding: 12,
        borderRadius: 6,
        overflowX: "auto",
        fontFamily: "monospace",
        fontSize: "var(--font-size-body-small)",
        lineHeight: 1.45,
        margin: "0 0 12px",
      }}
      {...props}
    />
  ),
  a: ({ href, ...rest }) => (
    <a
      href={SAFE_URL(href)}
      target="_blank"
      rel="noopener noreferrer"
      style={{ color: "var(--color-primary-content, #4e86db)", textDecoration: "underline" }}
      {...rest}
    />
  ),
  // Images are intentionally dropped to ``about:blank`` if the URL
  // looks suspicious. Most consent templates won't include images;
  // those that do can use a hosted https URL.
  img: ({ src, alt }) => <img src={SAFE_URL(src)} alt={alt ?? ""} style={{ maxWidth: "100%", margin: "8px 0" }} />,
  strong: (props) => <strong style={{ fontWeight: 600 }} {...props} />,
  em: (props) => <em style={{ fontStyle: "italic" }} {...props} />,
  hr: () => (
    <hr style={{ border: 0, borderTop: "1px solid var(--color-neutral-border, rgba(0,0,0,0.1))", margin: "16px 0" }} />
  ),
};

export const SafeMarkdown = ({ text }: { text: string }) => {
  return <ReactMarkdown components={components}>{text || ""}</ReactMarkdown>;
};
